import './styles.scss';

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useOfferClickoutService } from '@cx-modules/hooks';

import { type OffersGroupedIndexProps } from '../contract';

import TabContainer from './components/tab-container';
import { useCallbackWhenIntersected } from './hooks/useCallbackWhenIntersected';
import { useImpression } from './hooks/useImpression';
import { useQuery } from './hooks/useQuery';

const getDataLayer = () => {
  if (typeof window !== 'undefined') {
    return (window as Window & { dataLayer?: unknown[] }).dataLayer ?? [];
  }
  return [];
};

const OffersGrouped: React.FC<Omit<OffersGroupedIndexProps, 'dehydratedState'>> = props => {
  const { data } = useQuery(props);
  const { t } = useTranslation('label');

  const {
    analyticsDataLayer = getDataLayer(),
    analyticsImpressionEventData,
    apiKey,
    clickoutService = 'OFFERS_CLICKOUT_SERVICE',
    clickoutServiceUrl,
    deviceType = 'desktop',
    disableAnalytics = false,
    footerIcons,
    footerNotice,
    fullPageUrl,
    gtmId = null,
    initialOffersToShow = 3,
    pageUrl,
    placeholders = '',
    poweredBy,
    recommendedMarketId,
  } = props;

  const { trackImpression } = useImpression(disableAnalytics, analyticsDataLayer);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hasTrackedRef = useRef(false);

  useCallbackWhenIntersected(containerRef, () => {
    if (hasTrackedRef.current) return;
    hasTrackedRef.current = true;

    trackImpression({
      action: 'impression',
      category: 'Widget',
      event: 'widget_impression',
      label: 'widget_impression',
      page: fullPageUrl,
      variation: 'grouped-offers',
      widgetType: 'offers',
      ...(analyticsImpressionEventData ?? {}),
    });
  });

  const shouldUseClickoutService = clickoutService === 'OFFERS_CLICKOUT_SERVICE';

  const offers = useOfferClickoutService({
    apiKey,
    clickoutOrigin: 'offers-grouped',
    clickoutServiceUrl,
    deviceType,
    fullPageUrl,
    gtmId,
    offers: data,
    pageUrl,
    recommendedMarketId,
    shouldUseClickoutService,
  });

  if (!data || !offers) {
    return null;
  }

  const termsCtaLabel = t('CX_OFFERS_TERMS_CTA', { defaultValue: 'T&Cs' });

  return (
    <TabContainer
      analyticsDataLayer={analyticsDataLayer}
      containerRef={containerRef}
      disableAnalytics={disableAnalytics}
      footerIcons={footerIcons}
      footerNotice={footerNotice}
      fullPageUrl={fullPageUrl}
      geolocation={props.geolocation}
      initialOffersToShow={initialOffersToShow}
      offers={offers}
      placeholders={placeholders}
      poweredBy={poweredBy}
      termsCtaLabel={termsCtaLabel}
    />
  );
};

export default OffersGrouped;
